import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useTz } from "@/lib/TzContext";
import { useI18n } from "@/lib/I18nContext";
import { pickText } from "@/lib/bilingual";
import { Bell, X, Trash2 } from "lucide-react";

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const { format } = useTz();
  const { t, lang } = useI18n();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data?.items || []);
      setUnread(r.data?.unread || 0);
    } catch (err) {
      // user might not be authenticated yet; log only unexpected errors
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("NotificationsBell.load failed:", err.response.status, err.message);
      }
    }
  }, []);

  useEffect(() => {
    load();
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    const id = setInterval(load, 60000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(id);
    };
  }, [load]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try {
        await api.post("/notifications/mark-seen");
        setUnread(0);
      } catch (err) {
        console.warn("NotificationsBell.mark-seen failed:", err?.response?.status, err?.message);
      }
    }
  };

  const handleClick = (it) => {
    setOpen(false);
    if (it.link) navigate(it.link);
  };

  const dismissOne = async (e, it) => {
    e.stopPropagation();
    e.preventDefault();
    setItems((prev) => prev.filter((x) => x.id !== it.id));
    try {
      await api.post("/notifications/dismiss", { notification_id: it.id });
    } catch (err) {
      console.warn("NotificationsBell.dismiss failed:", err?.response?.status, err?.message);
      load();
    }
  };

  const dismissAll = async () => {
    if (items.length === 0) return;
    setItems([]);
    setUnread(0);
    try {
      await api.post("/notifications/dismiss-all");
    } catch (err) {
      console.warn("NotificationsBell.dismiss-all failed:", err?.response?.status, err?.message);
      load();
    }
  };

  const fmt = (iso) => {
    if (!iso) return "";
    return format(iso, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  const typeBg = {
    news: "var(--c-sky)",
    publish: "var(--c-lime)",
    result: "var(--c-yellow)",
  };

  return (
    <div className="relative">
      <button
        onClick={toggle}
        data-testid="notifications-bell"
        aria-label={t("notifications.aria")}
        className="relative w-10 h-10 md:w-11 md:h-11 border border-zinc-800 hover:border-[var(--c-lime)] transition-colors flex items-center justify-center"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-badge"
            className="absolute -top-1.5 -right-1.5 bg-[var(--c-red)] text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center leading-none"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-testid="notifications-backdrop" />
          <div
            data-testid="notifications-panel"
            className="absolute right-0 mt-2 w-80 sm:w-96 bg-[#0A0A0A] border border-zinc-800 shadow-2xl z-50 max-h-[70vh] overflow-y-auto"
          >
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display text-lg uppercase">{t("notifications.title")}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mt-0.5">
                  {t("notifications.count", { count: items.length })}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {items.length > 0 && (
                  <button
                    onClick={dismissAll}
                    data-testid="notifications-dismiss-all"
                    className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[var(--c-red)] border border-zinc-800 hover:border-[var(--c-red)] px-2 py-1 transition-colors"
                  >
                    {t("notifications.clearAll")}
                  </button>
                )}
                <button onClick={() => setOpen(false)} aria-label={t("common.close")} className="text-zinc-400 hover:text-white">
                  <X size={16} />
                </button>
              </div>
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-zinc-500">
                {t("notifications.empty")}
              </div>
            ) : (
              <ul>
                {items.map((it, i) => (
                  <li key={it.id || `${it.type}-${it.at}-${i}`} className="relative group">
                    <button
                      onClick={() => handleClick(it)}
                      data-testid={`notification-item-${i}`}
                      className="w-full text-left pl-4 pr-12 py-3 border-b border-zinc-900 hover:bg-zinc-900 flex items-start gap-3"
                    >
                      <span
                        className="w-1 self-stretch shrink-0"
                        style={{ background: typeBg[it.type] || "#fff" }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-semibold truncate">{pickText(it.title, lang) || it.title}</div>
                        {it.body && (
                          <div className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{pickText(it.body, lang) || it.body}</div>
                        )}
                        <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono-data mt-1">
                          {fmt(it.at)}
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => dismissOne(e, it)}
                      data-testid={`notification-dismiss-${i}`}
                      aria-label={t("notifications.dismiss")}
                      title={t("notifications.dismiss")}
                      className="absolute top-3 right-3 p-1.5 border border-zinc-800 text-zinc-500 hover:text-[var(--c-red)] hover:border-[var(--c-red)] opacity-60 hover:opacity-100 transition-all"
                    >
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
